/*
 * decaffeinate suggestions:
 * DS101: Remove unnecessary use of Array.from
 * DS102: Remove unnecessary code created because of implicit returns
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import {AddOptions, ModelSetOptions} from 'backbone';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import BenchlingCollection from 'BenchlingCollection';
import BenchlingConfig from 'BenchlingConfig';
import {PrimerPair, SequencePrimer} from 'BioJS';
import {Annotation} from 'biojs/Annotation';
import {DNA_COMPLEMENT_TABLE} from 'biojs/BioConstants';
import {Sequence, TranslationGroup} from 'biojs/Sequence';
import {Translation} from 'biojs/Translation';
import {ComponentsSequence} from 'components/Sequence';
import SequenceAttributeCollection from 'components/SequenceAttributeCollection';
import {NumberIDFolder} from 'constants/FolderConstants';
import * as MapsConstants from 'constants/MapsConstants';
import {OPPOSITE_STRAND, Strand} from 'constants/MolbioConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {PolymerFeatureModel} from 'constants/SequenceConstants';
import {SourceSummary} from 'constants/SourceConstants';
import * as mapsutil from 'molbio/maps/MapsUtil';
import {RegionSelectable, SelectableAttributes} from 'molbio/maps/selectables/Selectables';
import * as sequtil from 'molbio/SeqUtil';
import * as Colors from 'ui-library/Colors';

import * as util from './Util';

export interface PartJSON {
  name?: string;
  sequence?: {
    id: string;
    editURL: string;
    folder: NumberIDFolder;
    parts: Array<PartJSON>;
    source: SourceSummary;
    tagSchema: TagSchema | null;
  };
}

export interface PartAttributes extends SelectableAttributes {
  id: number;
  sequence: Sequence;
  // reverse and complement always have the same value.
  reverse?: boolean;
  complement?: boolean;
  sequence_fresh?: boolean;
  sequence_exists?: boolean;
  start: number;
  end: number;
  // Accessed by getLayersDict.
  [MapsConstants.LINEAR_LAYER_FULL]: number;
  [MapsConstants.LINEAR_LAYER_INLINE]: number;
  [MapsConstants.LINEAR_LAYER_INLINE_PDF]: number;
}

export interface Part extends RegionSelectable<ComponentsSequence> {
  get<K extends keyof PartAttributes>(key: K): PartAttributes[K];
  set(obj: Partial<PartAttributes>, options?: {silent?: boolean}): Part;
  set<K extends keyof PartAttributes>(key: K, value: PartAttributes[K], options?: ModelSetOptions): Part;
}

export class Part extends RegionSelectable<ComponentsSequence> {
  override collection!: Parts;

  override defaults(): Partial<PartAttributes> {
    return {
      reverse: false,
      complement: false,
    };
  }

  isFresh(): boolean {
    return Boolean(this.get('sequence_fresh'));
  }
  isDeleted(): boolean {
    return !this.get('sequence_exists');
  }

  isInSync(containerSequence: Sequence): boolean {
    const containerSequenceBases = util.slice(
      containerSequence.bases(),
      this.start(),
      this.end(),
      containerSequence.circular()
    );
    const sequenceBases =
      this.reversed() && this.complemented()
        ? util.revcomp(this.sequence().bases(), DNA_COMPLEMENT_TABLE)
        : this.sequence().bases();
    return containerSequenceBases.toLowerCase() === sequenceBases.toLowerCase();
  }

  isRevcomp(): boolean {
    return this.reversed() && this.complemented();
  }

  override length(containerSequence: ComponentsSequence): number {
    return sequtil.length(this.start(), this.end(), containerSequence.length());
  }

  sequence(): Sequence {
    return this.get('sequence');
  }
  reversed(): boolean {
    return Boolean(this.get('reverse'));
  }
  complemented(): boolean {
    return Boolean(this.get('complement'));
  }
  displayLabel(): string {
    return this.name();
  }

  name(includeRevcomp?: boolean): string {
    if (includeRevcomp == null) {
      includeRevcomp = true;
    }
    const sequence = this.get('sequence');
    let name = '';
    if (sequence != null) {
      name = sequence.name();
    }
    if (name == null) {
      name = '';
    }
    if (includeRevcomp) {
      const mods = [];
      if (this.reversed()) {
        mods.push('rev');
      }
      if (this.complemented()) {
        mods.push('comp');
      }
      if (mods.length > 0) {
        name = `${name} (${mods.join(' ')})`;
      }
    }
    return name;
  }

  hover(): boolean {
    return false;
  }

  uid(): string {
    return this.cid;
  }

  color(): string {
    // It's tricky to figure out what to do when part colors may conflict (e.g. an out of sync part in
    // version history is red but gets partially covered up by gray overlaps). To simplify they will all just
    // be filled gray, and out of syncness can be indicated by a warning or coloration on the part annotation
    // below the sequence map.
    if (BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS) {
      return Colors.GRAY;
    }

    if (this.isDeleted()) {
      return '#F55';
    } else if (this.isFresh()) {
      return Colors.GRAY;
    } else {
      return '#FAA';
    }
  }

  click(sequence: ComponentsSequence, shiftKey: boolean = false): void {
    const selection = sequence.get('selection');
    if (shiftKey) {
      selection.expandRange(this.start(), this.end());
    } else {
      selection.select(this);
    }
  }

  dblClick(sequence: ComponentsSequence, shiftKey: boolean = false): void {
    this.click(sequence, shiftKey);
  }

  override clone(containerSeqLength?: number, options?: {revcomp?: boolean}): Part {
    if (options == null) {
      options = {};
    }

    const part = new Part({
      start: this.get('start'),
      end: this.get('end'),
      sequence: this.get('sequence'),
      reverse: this.get('reverse'),
      complement: this.get('complement'),
    });

    if (options.revcomp) {
      assert(
        containerSeqLength != null,
        'Expected to receive container sequence length when cloning part with revcomp=true.'
      );
      const {start, end} = util.calculateRevcompForRangeFeature(containerSeqLength, part.start(), part.end());
      part.set({start, end, reverse: !part.reversed(), complement: !part.complemented()});
    }

    return part;
  }

  start(): number {
    return this.get('start');
  }

  end(): number {
    return this.get('end');
  }

  strand(): Strand {
    return 0;
  }

  override parse<T extends {sequence?: Sequence}>(response: T): T {
    const biojs = require('BioJS');
    if (response.sequence != null) {
      response.sequence = new biojs.Sequence(response.sequence, {parse: true});
    }
    return response;
  }

  override toJSON(): unknown {
    // eslint-disable-next-line no-restricted-syntax
    const ret = _.omit(super.toJSON(), 'sequence');

    // We want the server to know the sequence_id without sending the entire sequence
    // back to the server.
    ret.sequence_id = this.get('sequence').get('id');
    return ret;
  }

  toReactDict(): PartJSON {
    const sequence = this.get('sequence');
    return {
      name: this.name(false),
      sequence: {
        id: sequence.get('id'),
        editURL: sequence.get('editURL'),
        folder: sequence.folder()!,
        parts: sequence.get('parts').map((p: Part) => p.toReactDict()),
        source: sequence.folder()!.source!,
        tagSchema: sequence.get('tagSchema') ?? null,
      },
    };
  }

  type(): string {
    return 'part';
  }
}

export interface Parts {
  add(part: PartAttributes): Part;
  add(model: {} | Part, options?: AddOptions): Part;
  add(models: Array<{} | Part>, options?: AddOptions): Array<Part>;
}

export class Parts extends BenchlingCollection<Part> {
  static initClass(): void {
    this.prototype.model = Part;
    this.prototype.comparator = 'start';
  }

  getCount(): number {
    return this.length;
  }

  override initialize(): void {
    this.on('add remove reset', () => {
      this.setLayers();
      this.trigger('change:bases'); // TODO(BNCH-25393): Investigate whether this is still needed.
    });
  }

  partHasOverlaps(part: Part): boolean {
    assertAndContinue(this.models.includes(part), 'Expected to find part in this part collection');
    return this.getSequencePartsBetween(part.start(), part.end()).length > 1;
  }

  getSequencePartsAt(position: number): Array<Part> {
    const partsAt = this.models.filter((part) => sequtil.baseInRange(position, part.start(), part.end()));
    if (!BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS) {
      assertAndContinue(partsAt.length <= 1, 'Found multiple overlapping parts');
    }
    return partsAt;
  }

  getSequencePartsBetween(start: number, end: number): Array<Part> {
    return this.models.filter((part) =>
      sequtil.isRangeOverlap(part.get('start'), part.get('end'), start, end)
    );
  }

  getSequencePartsContainedWithin(containerSeqLength: number, start: number, end: number): Array<Part> {
    return this.getSequencePartsBetween(start, end).filter((p) =>
      sequtil.isCircularRangeWithin(p.start(), p.end(), start, end, containerSeqLength)
    );
  }

  // TODO still has bugs if startPart == endPart
  override clone(
    containerSeqLength?: number,
    start?: number,
    end?: number,
    options?: {revcomp?: boolean}
  ): this {
    // We need to make start and end optional to appease TS since it doesn't want us breaking the superclass
    // contract, but in practice it looks like they're always specified.
    start = start!;
    end = end!;
    containerSeqLength = containerSeqLength!;
    if (options == null) {
      options = {};
    }
    let parts = new Parts();

    const partsWithinRange = this.getSequencePartsContainedWithin(containerSeqLength, start, end);
    if (partsWithinRange.length === 0) {
      return parts as this;
    }
    for (const part of Array.from(partsWithinRange)) {
      parts.add(part.clone());
    }

    if (options.revcomp) {
      const revcompedParts = new Parts();
      for (const p of Array.from(parts.models)) {
        revcompedParts.add(p.clone(containerSeqLength, {revcomp: true}));
      }
      parts = revcompedParts;
    }

    return parts as this;
  }

  /**
   * Given a collection of features originating on a part sequence, create a new collection of
   * features with the position adjusted for the container sequence.
   */
  getUpdatedFeaturesFromPart<T extends PolymerFeatureModel>(
    part: Part,
    features: SequenceAttributeCollection<T>,
    index: number,
    containerSeqLen: number
  ): Array<T> {
    const updated: Array<T> = [];
    for (let feature of Array.from(features.models)) {
      if (feature.end() <= feature.start() && feature.end() !== 0) {
        continue;
      }
      feature = feature.clone();
      feature.set({fromSubPart: true});

      if (part.reversed()) {
        feature.set({strand: OPPOSITE_STRAND[feature.strand()]});
      }

      const partSeqLen = part.sequence().length();
      const featureStart = feature.start();
      const featureEnd = feature.end() === 0 ? partSeqLen : feature.end();

      const startAdjustedForRevcomp = part.reversed() ? partSeqLen - featureEnd : featureStart;
      const endAdjustedForRevcomp = part.reversed() ? partSeqLen - featureStart : featureEnd;

      const newFeatureStart = sequtil.mod(startAdjustedForRevcomp + index, containerSeqLen);
      const newFeatureEnd = sequtil.mod(endAdjustedForRevcomp + index, containerSeqLen);

      feature.set({start: newFeatureStart});
      feature.set({end: newFeatureEnd});

      // If a part is present multiple times on a Sequence then any features will be hoisted multiple times and
      // added to the biojs.Sequence model's appropriate feature Backbone Collection. Normally Backbone Collections
      // are deduped by `id`, so in order to avoid the features being incorrectly deduped, append the part id to the
      // feature id.
      feature.set({id: `${part.id}-${feature.id}`});

      if (feature instanceof SequencePrimer) {
        if (feature.get('bind_position') != null) {
          let bindPosition = feature.get('bind_position')!;
          if (part.reversed()) {
            bindPosition = partSeqLen - bindPosition - 1;
          }
          feature.set('bind_position', sequtil.mod(bindPosition + index, containerSeqLen));
        }
      }

      if (feature instanceof Translation) {
        const translationGroupId = feature.get('translationGroupId');
        if (translationGroupId != null) {
          feature.set({translationGroupId: `${part.id}-${translationGroupId}`});
        }
      }
      updated.push(feature);
    }

    return updated;
  }

  /**
   * Determine additional hoisted features to use from this part collection.
   *
   * Hoisted features have {fromSubPart: true} and have some differences in behavior, e.g. may not
   * be deleted.
   */
  getUpdatedFeatures<T extends PolymerFeatureModel>(
    containerSequence: Sequence,
    getFeatures: (s: Sequence) => SequenceAttributeCollection<T>
  ): Array<T> {
    // We don't want feature hoisting to result in duplicate features visible to the user, so skip
    // any features already on the sequence and deduplicate the same feature hoisted from parts
    // multiple times.
    const seenIdentityFingerprints = new Set<string>(
      getFeatures(containerSequence).models.map((feature) => feature.identityFingerprint())
    );

    const updated: Array<T> = [];
    for (const part of this.models) {
      if (!part.isInSync(containerSequence)) {
        // Out-of sync parts cannot hoist features because we wouldn't know where to position them.
        continue;
      }
      const componentSequenceFeatures = getFeatures(part.sequence());
      const newHoistedFeatures = this.getUpdatedFeaturesFromPart(
        part,
        componentSequenceFeatures,
        part.start(),
        containerSequence.length()
      );
      for (const feature of newHoistedFeatures) {
        const identityFingerprint = feature.identityFingerprint();
        if (seenIdentityFingerprints.has(identityFingerprint)) {
          // An equivalent feature already exists on the sequence, or was already added from a part.
          continue;
        }
        updated.push(feature);
        seenIdentityFingerprints.add(identityFingerprint);
      }
    }
    return updated;
  }

  private groupTranslationsByGroupId(
    translations: Array<Translation>
  ): Map<number | string | null, Array<Translation>> {
    const map = new Map<number | string | null, Array<Translation>>();

    translations.forEach((translation) => {
      const groupId = translation.get('translationGroupId') ?? null;
      if (!map.has(groupId)) {
        map.set(groupId, []);
      }
      map.get(groupId)!.push(translation);
    });

    return map;
  }

  private computeTranslationGroupIdentityFingerprint(
    translationGroup: TranslationGroup,
    translations: Array<Translation>
  ): string {
    const sortedTranslations = translations.sort((a, b) => a.start() - b.start());

    const translationRegionFingerprints = sortedTranslations.map((t) => t.identityFingerprint()).join(',');

    return `transl-${translationGroup.codonTranslationTable}:${translationGroup.coerceStartCodonToMethionine}:${translationRegionFingerprints}`;
  }

  private computeTranslationIdentityFingerprint(
    translation: Translation,
    translationGroups: Array<TranslationGroup>,
    translationsByGroupId: Map<number | string | null, Array<Translation>>
  ): string {
    const translationGroupId = translation.get('translationGroupId');
    const translationGroup = translationGroups.find((group) => group.id === translationGroupId);
    const translationsInGroup =
      translationGroupId != null ? translationsByGroupId.get(translationGroupId) : null;

    if (translationGroup == null || translationsInGroup == null) {
      return translation.identityFingerprint();
    }
    return `${translation.identityFingerprint()}:${this.computeTranslationGroupIdentityFingerprint(translationGroup, translationsInGroup)}`;
  }

  /*
  Determine translations to display on the container sequence, including de-duplicated translations hoisted from parts.

  Translation hoisting should account for translation group identity so that translation groups
  are de-duplicated as an entire unit.
  */
  getUpdatedTranslations(containerSequence: Sequence): Array<Translation> {
    const containerSequenceTranslations = containerSequence.translations();
    const containerSequenceTranslationGroups = containerSequence.translationGroups();
    const containerSequenceTranslationsByGroupId = this.groupTranslationsByGroupId(
      containerSequenceTranslations.models
    );
    const seenIdentityFingerprints = new Set<string>(
      containerSequenceTranslations.map((translation) =>
        this.computeTranslationIdentityFingerprint(
          translation,
          containerSequenceTranslationGroups,
          containerSequenceTranslationsByGroupId
        )
      )
    );

    const updated: Array<Translation> = [];
    // When hoisting translations from the component sequence, we update the part's translation group ID
    // (to account for parts that match the container sequence in multiple locations).
    // Because of this, although translation groups do not have associated locations, we need to get the
    // "hoisted" translation groups from the part with the updated IDs.
    const allHoistedTranslationGroups = this.getUpdatedTranslationGroupsFromParts(containerSequence);
    for (const part of this.models) {
      if (!part.isInSync(containerSequence)) {
        // Out-of sync parts cannot hoist features because we wouldn't know where to position them.
        continue;
      }

      const componentSequenceTranslations = part.sequence().translations();
      const hoistedTranslations = this.getUpdatedFeaturesFromPart(
        part,
        componentSequenceTranslations,
        part.start(),
        containerSequence.length()
      );

      const componentTranslationGroupsIds = hoistedTranslations.map((t) => t.get('translationGroupId'));
      const hoistedTranslationGroups = allHoistedTranslationGroups.filter((group) =>
        componentTranslationGroupsIds.includes(group.id)
      );
      const hoistedComponentTranslationsByGroupId = this.groupTranslationsByGroupId(hoistedTranslations);

      for (const translation of hoistedTranslations) {
        const identityFingerprint = this.computeTranslationIdentityFingerprint(
          translation,
          hoistedTranslationGroups,
          hoistedComponentTranslationsByGroupId
        );
        if (seenIdentityFingerprints.has(identityFingerprint)) {
          // An equivalent translation already exists on the sequence, or was already added from a part.
          continue;
        }
        updated.push(translation);
        seenIdentityFingerprints.add(identityFingerprint);
      }
    }
    return updated;
  }

  /**
   * Determine additional hoisted translation groups to use from this part collection.
   *
   * Hoisted translation groups have some differences in behavior, e.g. may not be deleted or edited.
   * Translation groups are not Backbone models and are not positional on their own (this is handled by
   * separately hoisted Translation models) so they do not need to be adjusted like other features.
   */
  getUpdatedTranslationGroupsFromParts(containerSequence: Sequence): Array<TranslationGroup> {
    const seenGroupIds = new Set<number | string>(
      containerSequence.translationGroups().map((group) => group.id)
    );
    const updated: Array<TranslationGroup> = [];
    for (const part of this.models) {
      if (!part.isInSync(containerSequence)) {
        // Out-of sync parts cannot hoist translation groups because the child translations
        // are also not hoisted from out-of sync parts.
        continue;
      }
      const translationGroups = part.sequence().translationGroups();
      for (const group of translationGroups) {
        const uniqueTranslationGroupId = `${part.id}-${group.id}`;
        if (seenGroupIds.has(uniqueTranslationGroupId)) {
          assertAndContinue(false, 'unexpectedly encountered same group twice');
          // This translation group is already on the sequence or added from a part
          continue;
        }
        updated.push({...group, id: uniqueTranslationGroupId, fromSubPart: true});
        seenGroupIds.add(uniqueTranslationGroupId);
      }
    }
    return updated;
  }

  annotations(containerSequence: Sequence): Array<Annotation> {
    return this.getUpdatedFeatures(containerSequence, (seq) => seq.annotations());
  }

  translations(containerSequence: Sequence): Array<Translation> {
    return this.getUpdatedTranslations(containerSequence);
  }

  translationGroups(containerSequence: Sequence): Array<TranslationGroup> {
    return this.getUpdatedTranslationGroupsFromParts(containerSequence);
  }

  sequencePrimers(containerSequence: Sequence): Array<SequencePrimer> {
    return this.getUpdatedFeatures(containerSequence, (seq) => seq.sequencePrimers());
  }

  /**
   * Determine additional hoisted primer pairs to use from this part collection.
   *
   * Because primer pairs only have primer_fwd_id and primer_rev_id attributes rather than
   * referencing the actual primer models, we cannot determine which pairs need to be deduped here.
   * Duplicate pairs are instead cleaned up in sequence initialization.
   */
  primerPairs(containerSequence: Sequence): Array<PrimerPair> {
    const updated: Array<PrimerPair> = [];
    for (const part of this.models) {
      if (!part.isInSync(containerSequence)) {
        // Out-of sync parts cannot hoist primer pairs because their linked primers
        // are also not hoisted from out-of sync parts.
        continue;
      }
      for (const pair of part.sequence().primerPairs().models) {
        const pairCopy = pair.clone() as PrimerPair;
        pairCopy.set({
          fromSubPart: true,
          id: `${part.id}-${pairCopy.id}`,
          primer_fwd_id: `${part.id}-${pairCopy.get('primer_fwd_id')}`,
          primer_rev_id: `${part.id}-${pairCopy.get('primer_rev_id')}`,
        });
        updated.push(pairCopy);
      }
    }
    return updated;
  }

  setFullLayers(): void {
    const modelsAndLayers = mapsutil.getModelLayers(this.models, {top: false});
    for (const {model, layer} of modelsAndLayers) {
      model.set(MapsConstants.LINEAR_LAYER_FULL, layer, {silent: true});
    }
  }

  setLayers(): void {
    this.setFullLayers();
    this.trigger('layerChange');
  }

  toReactJSON(): Array<PartJSON> {
    return this.models.map((p) => p.toReactDict());
  }
}
Parts.initClass();

export {Part as Model, Parts as Collection};
